import { each, bind, isEqual, isEmpty } from 'lodash';
import type { Store } from '@reduxjs/toolkit';
import { _ } from '@splunk/ui-utils/i18n';
import {
    isBaseDataSource,
    isChainDataSource,
    createBaseChainModel,
    getChainDataSources,
    getBaseDataSourceId,
} from '@splunk/datasource-utils';
import {
    MAX_CHAIN_LENGTH,
    RUNNING_SEARCH_STATUSES,
} from '@splunk/dashboard-utils';
import { triggerEvent } from '@splunk/dashboard-state';
import type {
    BroadcastEventCallback,
    BroadcastEventArgs,
    DataSourceEventPayload,
    ExtendableDataSourceDefinition,
    PresetUtility,
    RootDataSourcesDefinition,
    IDataSourceRegistry,
    SearchCountsInfo,
} from '@splunk/dashboard-types';

import { sprintf } from '@splunk/ui-utils/format';
import { computeDSHash } from '../utils';
import DataSourceController, {
    type ControlOptionTypes,
} from './DataSourceController';
import RiskyCommandController from './RiskyCommandController';

type DataSourceContext = Record<string, unknown>;

type DataSourceEventArgs = BroadcastEventArgs<DataSourceEventPayload>;

/**
 * A module that create reuseable DataSource controller
 */
class DataSourceRegistry implements IDataSourceRegistry<
    DataSourceController,
    DataSourceContext
> {
    definition: Record<string, ExtendableDataSourceDefinition>;

    preset: PresetUtility;

    store: Store;

    dataSourceContext?: DataSourceContext;

    controllers: Record<number, DataSourceController>;

    riskyCommandController: RiskyCommandController;

    broadcastCallback: BroadcastEventCallback;

    /**
     * Creates a registry for data source controllers and initializes
     * default event broadcasting behavior.
     *
     * @param args - Constructor arguments.
     * @param args.preset - Preset utility used to resolve data source metadata.
     * @param args.dataSourceContext - Optional shared context passed to controllers.
     */
    constructor({
        preset,
        dataSourceContext,
    }: {
        preset: PresetUtility;
        dataSourceContext?: DataSourceContext;
    }) {
        this.preset = preset;
        this.dataSourceContext = dataSourceContext;
        this.definition = {};
        this.controllers = {};
        this.store = null as unknown as Store;

        this.riskyCommandController = new RiskyCommandController();

        // The default behavior is that the event handler
        // triggers the event and dispatches to the store
        this.broadcastCallback = ({
            eventType,
            targetId,
            payload,
        }: DataSourceEventArgs) => {
            const dispatchPayload: Record<string, unknown> = {
                ...payload,
                tokenNamespace: targetId
                    ? this.preset?.getDataSourceName?.(
                          this.definition?.[targetId]
                      )
                    : undefined,
            };

            // If enableSmartSources option exists on the DS definition (or in
            // the original definition of an errored snapshot) then include the
            // flag in the dispatch payload so it can be validated in the handler
            // NOTE: The FF check should be on the handler and not needed here
            const definition = this.definition[targetId ?? ''];
            if (
                definition?.options?.enableSmartSources ||
                (
                    definition?.options?.meta as {
                        originalDefinition?: ExtendableDataSourceDefinition;
                    }
                )?.originalDefinition?.options?.enableSmartSources
            ) {
                dispatchPayload.enableSmartSources = true;
            }

            this.store?.dispatch(
                triggerEvent(targetId, eventType, dispatchPayload)
            );
        };
    }

    /**
     * Updates the shared context used for newly allocated data source controllers.
     *
     * @param ctx - Arbitrary runtime context for data sources.
     */
    setDataSourceContext(ctx: DataSourceContext) {
        this.dataSourceContext = ctx;
    }

    /**
     * Replaces the preset utility used by this registry.
     *
     * @param preset - Preset utility containing data source type definitions.
     */
    setPreset(preset: PresetUtility) {
        this.preset = preset;
    }

    /**
     * Subscribes the registry to a Redux store for event dispatching.
     *
     * @param store - Redux store used to dispatch data source events.
     */
    subscribeToStore(store: Store): void {
        this.store = store;
    }

    /**
     * Replaces the data source definition map and selectively reallocates
     * base/chain data source controllers that changed and are active in chain usage.
     *
     * @param args - Update arguments.
     * @param args.definition - New root data source definition map.
     * @param args.consumersInLayout - Optional set of consumer IDs currently in layout.
     */
    updateDefinition({
        definition,
        consumersInLayout,
    }: {
        definition: RootDataSourcesDefinition;
        consumersInLayout?: string[];
    }): void {
        const lastDefinition = this.definition;
        this.definition = definition as Record<
            string,
            ExtendableDataSourceDefinition
        >;
        // The following code detects if any base/chain data source without any UI component binding has been updated in the new definition.
        // If there are such data sources, the following code triggers the allocate() function to create new data source and remap into base chain tree
        Object.entries(lastDefinition).forEach(([dsId, dsDef]) => {
            const newDataSourceDef = this.definition?.[dsId];
            if (isEqual(dsDef, newDataSourceDef)) {
                // definition is unchanged
                return;
            }

            const controllerHash = computeDSHash(dsDef);
            const oldDataSourceController = this.controllers[controllerHash];
            if (!oldDataSourceController) {
                // this data source has never been created
                return;
            }

            if (
                !isBaseDataSource(dsId, this.definition) &&
                !isChainDataSource(dsId, this.definition)
            ) {
                // this data source is not used in base chain
                return;
            }

            const oldDSSubscriptions =
                oldDataSourceController.getDataSourceSubscriptions();
            const oldSubscriptions = oldDataSourceController.getSubscriptions();

            const nonDataSourceConsumers = new Set();
            Array.from(oldSubscriptions.keys()).forEach((subId) => {
                if (oldDSSubscriptions.has(subId)) {
                    // ignore datasource subscriptions
                    return;
                }

                if (!consumersInLayout?.length) {
                    // when no known consumers are in the layout, track everything
                    nonDataSourceConsumers.add(subId);
                    return;
                }

                if (consumersInLayout.includes(subId)) {
                    // track non-datasource subscriptions. if consumers are known
                    // for the layout, only include subscriptions in the layout.
                    nonDataSourceConsumers.add(subId);
                }
            });

            if (nonDataSourceConsumers.size) {
                // controller was not just used for datasource subscriptions;
                // there is associated UI content in the current layout
                return;
            }

            const chainDataSources = this.getActiveChainControllers(dsId);
            if (!Object.keys(chainDataSources).length) {
                // the datasource hasn't been chained, it doesn't need to run
                return;
            }

            // call allocate() to create new data source and remap into current base chain
            this.allocate({ dataSourceId: dsId });
        });
    }

    /**
     * Returns an existing controller matching the data source definition hash,
     * or allocates and wires a new one.
     *
     * @param args - Allocation arguments.
     * @param args.dataSourceId - Data source identifier to allocate.
     * @returns Matching or newly created data source controller.
     */
    allocate = ({
        dataSourceId,
    }: {
        dataSourceId: string;
    }): DataSourceController => {
        if (!dataSourceId) {
            throw new Error(
                'dataSourceId is required to allocate DataSourceController'
            );
        }

        // allocate a DataSource controller that matches the definition.
        // an existing controller will be returned if definition matches,
        // otherwise a new one will be created and return.
        const dataSourceDef = this.getDataSourceDefOrThrow(dataSourceId);
        const key = computeDSHash(dataSourceDef);
        const existingController = this.controllers[key];

        if (existingController) {
            existingController.addAdditionalDataSourceId(dataSourceId);
            return existingController;
        }

        const newController = this.createDataSourceController(
            dataSourceId,
            dataSourceDef,
            key
        );
        this.setupControllerRelationships(
            newController,
            dataSourceId,
            dataSourceDef
        );
        this.controllers[key] = newController;

        return newController;
    };

    /**
     * Looks up the definition for a data source and throws if it is missing/empty.
     *
     * @param dataSourceId - Data source identifier to resolve.
     * @returns Resolved data source definition.
     */
    private getDataSourceDefOrThrow(
        dataSourceId: string
    ): ExtendableDataSourceDefinition {
        // We resolve tokens and apply defaults; using definition keeps behavior
        // aligned with existing allocation flow.
        const dataSourceDef = this.definition[dataSourceId];
        if (isEmpty(dataSourceDef)) {
            throw new Error(
                `cannot find dataSourceDef for dataSourceId: ${dataSourceId}`
            );
        }

        return dataSourceDef;
    }

    /**
     * Instantiates a new data source controller and attaches teardown/event handlers.
     *
     * @param dataSourceId - Data source identifier for the controller.
     * @param dataSourceDef - Data source definition used to construct the controller.
     * @param key - Definition hash key used by the registry cache.
     * @returns Newly constructed data source controller.
     */
    private createDataSourceController(
        dataSourceId: string,
        dataSourceDef: ExtendableDataSourceDefinition,
        key: number
    ): DataSourceController {
        const newController = new DataSourceController({
            id: dataSourceId,
            dataSourceDef,
            dataSourceContext: this.dataSourceContext,
            preset: this.preset,
            riskyCommandController: this.riskyCommandController,
        });

        newController.onTeardown(
            bind(this.handleDataSourceTeardown, this, key)
        );
        newController.onDataSourceEvent(this.broadcastDataSourceEvent);

        return newController;
    }

    /**
     * Applies base/chain relationship setup rules to a new controller.
     *
     * @param newController - Newly allocated controller instance.
     * @param dataSourceId - Data source identifier for the controller.
     * @param dataSourceDef - Definition associated with the controller.
     */
    private setupControllerRelationships(
        newController: DataSourceController,
        dataSourceId: string,
        dataSourceDef: ExtendableDataSourceDefinition
    ): void {
        if (isBaseDataSource(dataSourceId, this.definition)) {
            this.setupBaseDataSourceController(newController, dataSourceId);
            return;
        }

        if (isChainDataSource(dataSourceId, this.definition)) {
            this.setupChainDataSourceController(
                newController,
                dataSourceId,
                dataSourceDef
            );
        }
    }

    /**
     * Sets base chain model and reparents active chain controllers to this base.
     *
     * @param newController - Base data source controller.
     * @param dataSourceId - Base data source identifier.
     */
    private setupBaseDataSourceController(
        newController: DataSourceController,
        dataSourceId: string
    ): void {
        newController.setBaseChainModel(
            createBaseChainModel(dataSourceId, this.definition)
        );

        this.reparentChainControllers(dataSourceId, newController);
    }

    /**
     * Validates and wires a chain data source to its parent/base relationships.
     *
     * @param newController - Chain data source controller.
     * @param dataSourceId - Chain data source identifier.
     * @param dataSourceDef - Chain data source definition.
     */
    private setupChainDataSourceController(
        newController: DataSourceController,
        dataSourceId: string,
        dataSourceDef: ExtendableDataSourceDefinition
    ): void {
        this.dataSourceValidation(dataSourceId);
        const parentDataSourceId = dataSourceDef.options?.extend as string;
        const parentController =
            this.getOrAllocateParentControllerForChainDataSource(
                dataSourceId,
                parentDataSourceId
            );

        newController.setParent(parentController);
        this.reparentChainControllers(dataSourceId, newController);
        this.refreshBaseChainModelForChainDataSource(dataSourceId);
    }

    /**
     * Finds or allocates the parent controller for a chain data source.
     * Also handles the single-subscription cancellation override edge case.
     *
     * @param dataSourceId - Chain data source identifier being allocated.
     * @param parentDataSourceId - Parent data source identifier from `extend`.
     * @returns Parent controller for the chain data source.
     */
    private getOrAllocateParentControllerForChainDataSource(
        dataSourceId: string,
        parentDataSourceId: string
    ): DataSourceController {
        const parentDataSourceDef = this.definition[parentDataSourceId];
        if (!parentDataSourceId || !parentDataSourceDef) {
            throw new Error(
                _(`Cannot find parent dataSource: ${parentDataSourceId}`)
            );
        }

        const parentController =
            this.getDataSourceController(parentDataSourceId);
        if (!parentController) {
            return this.allocate({ dataSourceId: parentDataSourceId });
        }

        const subs = parentController.getSubscriptions();
        // Special case:
        // if parent has only one data source subscription, and this
        // subscription is the current newController
        if (subs.size === 1 && subs.has(dataSourceId)) {
            parentController.setHandleSubscriptionCancelOverride();
        }

        return parentController;
    }

    /**
     * Reparents active chain controllers to a new parent controller and
     * resubscribes them to parent updates.
     *
     * @param parentDataSourceId - Identifier whose chain descendants should be updated.
     * @param parentController - Controller that should become the new parent.
     */
    private reparentChainControllers(
        parentDataSourceId: string,
        parentController: DataSourceController
    ): void {
        const chainControllers =
            this.getActiveChainControllers(parentDataSourceId);
        // Mapping chain dataSources triggers setup on the child controller.
        each(chainControllers, (controller) => {
            controller.setParent(parentController);
            controller.subscribeToParentDataSourceController();
        });
    }

    /**
     * Recomputes and updates the base chain model for a chain data source's base,
     * when both base ID and base controller are available.
     *
     * @param dataSourceId - Chain data source identifier.
     */
    private refreshBaseChainModelForChainDataSource(
        dataSourceId: string
    ): void {
        const baseDataSourceId = getBaseDataSourceId(
            dataSourceId,
            this.definition
        );
        if (!baseDataSourceId) {
            return;
        }

        const baseDataSourceController =
            this.getDataSourceController(baseDataSourceId);
        if (!baseDataSourceController) {
            return;
        }

        baseDataSourceController.setBaseChainModel(
            createBaseChainModel(baseDataSourceId, this.definition)
        );
    }

    /**
     * Returns the controller for a data source ID using the current definition hash.
     *
     * @param dataSourceId - Data source identifier to resolve.
     * @returns Matching controller if present, otherwise `undefined`.
     */
    getDataSourceController = (
        dataSourceId: string
    ): DataSourceController | undefined =>
        this.controllers[computeDSHash(this.definition[dataSourceId])];

    /**
     * Removes a controller entry from the registry cache.
     *
     * @param key - Controller cache key (definition hash).
     */
    handleDataSourceTeardown = (key: number): void => {
        delete this.controllers[key];
    };

    /**
     * Pauses all controllers or a filtered set of controllers by data source ID.
     *
     * @param dataSourceIds - Optional data source IDs to pause; all if omitted.
     * @param options - Optional pause control flags.
     * @param options.checkIfInUse - When true, skip pausing if controller is in use.
     * @returns Promise resolving to settled results for each pause attempt.
     */
    pauseDataSources = (
        dataSourceIds?: string[],
        { checkIfInUse }: ControlOptionTypes = {}
    ): Promise<PromiseSettledResult<void>[]> => {
        const controllers: DataSourceController[] = Array.isArray(dataSourceIds)
            ? this.getControllersByIds(dataSourceIds)
            : Object.values(this.controllers);

        return Promise.allSettled(
            controllers.map((controller) =>
                controller.supportsControlActions()
                    ? controller.pause({ checkIfInUse })
                    : Promise.resolve()
            )
        );
    };

    /**
     * Resumes all controllers or a filtered set of controllers by data source ID.
     *
     * @param dataSourceIds - Optional data source IDs to resume; all if omitted.
     * @returns Promise resolving to settled results for each resume attempt.
     */
    resumeDataSources = (
        dataSourceIds?: string[]
    ): Promise<PromiseSettledResult<void>[]> => {
        const controllers: DataSourceController[] = Array.isArray(dataSourceIds)
            ? this.getControllersByIds(dataSourceIds)
            : Object.values(this.controllers);

        return Promise.allSettled(
            controllers.map((controller) =>
                controller.supportsControlActions()
                    ? controller.resume()
                    : Promise.resolve()
            )
        );
    };

    /**
     * Broadcasts a data source event through the configured callback.
     *
     * @param event - Event payload emitted by a controller.
     */
    broadcastDataSourceEvent = (event: BroadcastEventArgs): void => {
        if (this.broadcastCallback) {
            this.broadcastCallback(event);
        }
    };

    /**
     * Registers a callback used to broadcast data source events.
     *
     * @param callback - Event broadcast handler.
     */
    onEventBroadcast = (callback: BroadcastEventCallback): void => {
        this.broadcastCallback = callback;
    };

    /**
     * Captures last-known subscription state for all active controllers.
     *
     * @returns Snapshot keyed by controller ID and additional data source IDs.
     */
    snapshot() {
        const state: ReturnType<IDataSourceRegistry['snapshot']> = {};
        each(this.controllers, (controller) => {
            const subs = controller.getSubscriptions();
            const additionalDataSourceIds =
                controller.getAdditionalDataSourceIds();
            const subState: Record<string, unknown> = {};
            subs.forEach((sub) => {
                subState[sub.consumerId] = sub.getLastState();
            });
            state[controller.id] = subState;
            additionalDataSourceIds.forEach((dsId) => {
                state[dsId] = subState;
            });
        });
        return state;
    }

    /**
     * Returns active chain controllers currently extending a parent data source.
     *
     * @param parentDataSourceId - Parent data source identifier.
     * @returns Map of child data source ID to active controller.
     */
    getActiveChainControllers(
        parentDataSourceId: string
    ): Record<string, DataSourceController> {
        if (!parentDataSourceId) {
            return {};
        }

        const chainDataSourceDefs = getChainDataSources(
            parentDataSourceId,
            this.definition
        );

        const result: Record<string, DataSourceController> = {};

        each(chainDataSourceDefs, (chainDataSourceDef, chainDataSourceId) => {
            const chainDataSourceController =
                this.getDataSourceController(chainDataSourceId);
            if (chainDataSourceController) {
                result[chainDataSourceId] = chainDataSourceController;
            }
        });

        return result;
    }

    /**
     * Validates that a chain data source has a valid parent path and respects
     * chain constraints (no cycles, max depth, and allowed parent/child types).
     *
     * @param dataSourceId - Data source identifier to validate.
     */
    dataSourceValidation(dataSourceId: string): void {
        if (!isChainDataSource(dataSourceId, this.definition)) {
            return;
        }

        let references = 0;
        let currentId = dataSourceId;
        let currentDS = this.definition[currentId];
        const visitedDS = [];
        const extend = currentDS?.options?.extend ?? '';

        if (!this.definition[extend]) {
            throw new Error(
                _(
                    `Chain data source ${dataSourceId} must have valid extend property`
                )
            );
        }
        const dataSourcesPreset = this.preset.getPresetDefinition().dataSources;
        let chainDsType: string = currentDS.type;
        let chainDsId: string | null = null;

        while (currentDS != null) {
            const { isChainEnabled, extendableByDataSourceTypes = [] } =
                dataSourcesPreset?.[currentDS.type]?.config ?? {};

            if (visitedDS.indexOf(currentId) > -1) {
                throw new Error(
                    sprintf(
                        _(
                            'Data source %(dataSourceId)s creates an invalid circular reference; check parent data sources referenced by extend property.'
                        ),
                        { dataSourceId: currentId }
                    )
                );
            }
            if (references > MAX_CHAIN_LENGTH) {
                throw new Error(
                    sprintf(
                        _(
                            '%(numReferences)s chained data sources exceeds the maximum of %(maxChainLength)s'
                        ),
                        {
                            numReferences: references,
                            maxChainLength: MAX_CHAIN_LENGTH,
                        }
                    )
                );
            }

            if (
                chainDsId &&
                !extendableByDataSourceTypes.includes(chainDsType)
            ) {
                throw new Error(
                    sprintf(
                        _(
                            'Data source %(dataSourceId)s, of type %(dataSourceType)s, cannot be a parent search for data source %(chainDataSourceId)s, of type %(chainDataSourceType)s. Create a new search instead.'
                        ),
                        {
                            dataSourceId: currentId,
                            dataSourceType: currentDS.type,
                            chainDataSourceId: chainDsId,
                            chainDataSourceType: chainDsType,
                        }
                    )
                );
            }

            if (isBaseDataSource(currentId, this.definition)) {
                return;
            }

            if (!isChainEnabled) {
                throw new Error(
                    sprintf(
                        _(
                            'Data source %(dataSourceId)s, of type %(dataSourceType)s, cannot be a chain data source.'
                        ),
                        {
                            dataSourceId: currentId,
                            dataSourceType: currentDS.type,
                        }
                    )
                );
            }

            chainDsId = currentId;
            chainDsType = currentDS.type;
            visitedDS.push(currentId);
            references += 1;
            currentId = currentDS?.options?.extend ?? '';
            currentDS = this.definition[currentId];
        }
    }

    /**
     * Returns the count of unique search jobs and their completion status.
     *
     * @returns Object with total, running, and completed counts of unique searches.
     */
    getSearchCounts(): SearchCountsInfo {
        const controllers = Object.values(this.controllers);

        const { total, running, realTimeRunning } = controllers.reduce(
            (acc, controller) => {
                const status = controller.getStatus();
                if (!status) {
                    return acc;
                }

                acc.total += 1;
                if (RUNNING_SEARCH_STATUSES.includes(status)) {
                    acc.running += 1;
                    if (controller.getDataSourceMetaData()?.isRealTimeSearch) {
                        acc.realTimeRunning += 1;
                    }
                }

                return acc;
            },
            { total: 0, running: 0, realTimeRunning: 0 }
        );

        return { total, running, completed: total - running, realTimeRunning };
    }

    /**
     * Returns the registry cache of allocated controllers.
     *
     * @returns Controller cache keyed by data source definition hash.
     */
    getDataSourceControllers(): Record<string, DataSourceController> {
        return this.controllers;
    }

    /**
     * Tears down all currently allocated controllers.
     */
    teardown(): void {
        const controllers = Object.values(this.controllers);
        controllers.forEach((controller) => controller.teardown());
    }

    /**
     * Returns controllers directly or indirectly associated with provided IDs.
     *
     * @param dataSourceIds - Data source IDs to match against controller IDs and aliases.
     * @returns Matching controller instances.
     */
    private getControllersByIds(dataSourceIds: string[]) {
        return Object.values(this.controllers).reduce<DataSourceController[]>(
            (acc, controller) => {
                const additionalIds = controller.getAdditionalDataSourceIds();

                if (
                    dataSourceIds.some(
                        (id) => id === controller.id || additionalIds.has(id)
                    )
                ) {
                    acc.push(controller);
                }

                return acc;
            },
            []
        );
    }
}

export default DataSourceRegistry;
